/**
 * AI alert performance — what every idea actually did.
 *
 * This is the evidence for the decision the user actually wants to make: should the AI replace the
 * existing alerting? The page argues that question should be answered PER GRADE rather than on one
 * blended average, and shows the per-grade sample sizes next to the claim so the argument can be
 * checked rather than taken on trust.
 *
 * FOUR HONESTY RULES, EACH OF WHICH COSTS SOMETHING TO KEEP
 *
 * 1. EVERY FIGURE IS SIGNED BY DIRECTION. "Best %" is the best the POSITION got to, so a short
 *    that fell shows a gain. The raw price excursion is not shown anywhere, because reading it as
 *    "max return" was wrong for 302 of 449 directional candidates — the defect this view exists on
 *    the other side of.
 *
 * 2. MATURITY IS ON EVERY ROW, NOT IN A FOOTNOTE. A row measured over one closed bar sits next to
 *    one measured over ten, and the table says which is which. Anything under the horizon is
 *    visibly greyed rather than quietly averaged in.
 *
 * 3. THE PEAK COMES IN TWO FLAVOURS AND THEY ARE LABELLED. Close-based is a price the market
 *    actually printed at a bar boundary — something you could plausibly have exited at. High-based
 *    is the intraday extreme, which is real but not reliably obtainable. Collapsing them into one
 *    "max return" would flatter the record.
 *
 * 4. DUPLICATES ARE FOLDED, AND SAY SO. Several runs a day scan the same symbol against the same
 *    closed bar; those are one decision, and a "seen 3x" marker beats three identical rows
 *    tripling their own weight in every average.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Chip, Button, Tooltip, CircularProgress, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Alert as MuiAlert, TableSortLabel,
  ToggleButton, ToggleButtonGroup, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import fmt from '../../utils/priceFormat';
import aiService, {
  type AiPerformanceReport, type AiPerformanceRow, type AiPerformanceBucket,
} from '../../services/ai.service';
import api from '../../services/api';
import HelpLink from '../common/HelpLink';
import AiReliabilityPanel from './AiReliabilityPanel';

type SortKey = 'createdAt' | 'symbol' | 'grade' | 'bestPct' | 'worstPct' | 'daysToBest' | 'relPct' | 'barsAvailable';

const STATE_LABEL: Record<string, string> = {
  BUY_NOW: 'Act now',
  BUY_ON_PULLBACK: 'On pullback',
  WAIT_FOR_CONFIRMATION: 'Wait',
  WATCH: 'Watch',
  AVOID: 'Avoid',
};

const GRADE_RANK: Record<string, number> = { A: 7, 'A-': 6, 'B+': 5, B: 4, 'B-': 3, C: 2, D: 1 };

const AiPerformancePanel: React.FC = () => {
  const { C } = useAppTheme();
  const [data, setData] = useState<AiPerformanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * 5d is the horizon that matters for a swing book, so it stays the intent — but on a book only
   * a few days old NOTHING has five closed bars, and opening there renders every benchmark-relative
   * cell as a dash. The first response says which horizons actually have data; the longest of those
   * is selected once, and only if the user has not already chosen. As the book matures this walks
   * itself back up to 5d and then 10d without a code change.
   */
  const [horizon, setHorizon] = useState<1 | 5 | 10>(5);
  // A ref, not state: `load` reads it, and putting it in that callback's dependency array would
  // change the callback's identity the moment the user clicks a horizon and fire a second fetch
  // for data already on screen. Nothing renders from it.
  const horizonPicked = useRef(false);
  const [grade, setGrade] = useState('');
  const [direction, setDirection] = useState('');
  const [symbol, setSymbol] = useState('');
  /**
   * Open on rows that HAVE a measurement.
   *
   * The newest night's candidates cannot be measured for ~36 hours (Delta stamps daily bars at
   * 00:00 UTC, the run fires at 12:30), so a strictly chronological default put ~98 rows of
   * nothing-but-dashes at the top and the page looked broken while being perfectly correct.
   * Defaulting to 1+ bars is not hiding them: the filter visibly reads "1+ closed bars", the
   * maturity ledger above still counts every row including the unmeasurable ones, and one click
   * returns to "Any maturity".
   */
  const [minBars, setMinBars] = useState(1);
  const [surfacedOnly, setSurfacedOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDesc, setSortDesc] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    aiService.getPerformance({
      horizon, grade: grade || undefined, direction: direction || undefined,
      symbol: symbol || undefined, minBars: minBars || undefined,
      surfacedOnly: surfacedOnly || undefined,
    })
      .then(r => {
        setData(r);
        // Only ever adjusts the INITIAL view. Once the user picks a horizon it is theirs.
        if (r && !horizonPicked.current && !(r.horizonsWithData || []).includes(horizon)) {
          const best = (r.horizonsWithData || [])[0];
          if (best === 1 || best === 5 || best === 10) setHorizon(best);
        }
      })
      .catch(e => setError(e?.response?.data?.error?.message || 'Could not load performance history'))
      .finally(() => setLoading(false));
  }, [horizon, grade, direction, symbol, minBars, surfacedOnly]);

  useEffect(() => {
    const t = setTimeout(load, symbol ? 350 : 0);   // debounce only the free-text filter
    return () => clearTimeout(t);
  }, [load, symbol]);

  const rows = useMemo(() => {
    const r = [...(data?.rows || [])];
    const val = (x: AiPerformanceRow): any => {
      if (sortKey === 'grade') return GRADE_RANK[x.grade || ''] ?? -1;
      return (x as any)[sortKey];
    };
    r.sort((a, b) => {
      const va = val(a), vb = val(b);
      // Nulls always sink, whichever way the column is sorted: a missing measurement is not a
      // small one, and letting it float to the top of a "best return" sort would be misleading.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDesc ? -c : c;
    });
    return r;
  }, [data, sortKey, sortDesc]);

  const sortBy = (k: SortKey) => {
    if (k === sortKey) setSortDesc(d => !d);
    else { setSortKey(k); setSortDesc(true); }
  };

  const exportCsv = async () => {
    setDownloading(true);
    try {
      // Fetched through the same authenticated client as everything else rather than an anchor
      // href — a bare link would be unauthenticated and return the login page as a .csv.
      // The FULL filter set, so the file reconciles against the table it was exported from.
      const res = await api.get(aiService.performanceCsvUrl({
        horizon, grade: grade || undefined, direction: direction || undefined,
        symbol: symbol || undefined, minBars: minBars || undefined,
        surfacedOnly: surfacedOnly || undefined,
      }), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data as any], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-alert-performance-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not export the CSV');
    } finally { setDownloading(false); }
  };

  const pctColor = (v: number | null) =>
    v == null ? C.dim : v > 0 ? C.bull : v < 0 ? C.bear : C.dim;

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const counts = data?.counts || { COMPLETE: 0, MATURING: 0, PENDING: 0, UNSCOREABLE: 0 };
  const scored = counts.COMPLETE + counts.MATURING;
  const minN = data?.minSampleForVerdict ?? 30;
  const readyGrades = (data?.byGrade || []).filter(g => g.n >= minN);

  return (
    <Box>
      {error && <MuiAlert severity="error" sx={{ mb: 2 }}>{error}</MuiAlert>}

      {/* ── What this is for, stated up front ─────────────────────────────── */}
      <MuiAlert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
          Every percentage here is signed by the direction the idea proposed — a short that fell
          shows a gain, not a loss. It is the best and worst the <b>position</b> reached, not the
          raw price move.
        </Typography>
        {readyGrades.length === 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            No grade has reached {minN} resolved outcomes yet, so nothing here should decide
            whether the AI replaces your existing alerting. {scored} of{' '}
            {scored + counts.PENDING + counts.UNSCOREABLE} rows carry a measurement so far.
          </Typography>
        )}
      </MuiAlert>

      {/* ── Filters ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={horizon}
          onChange={(_, v) => { if (v) { setHorizon(v); horizonPicked.current = true; } }}>
          {([1, 5, 10] as const).map(h => (
            // MUI's default unselected ToggleButton text measures 4.48:1 on this surface in light
            // mode — two hundredths under AA, which is a fail. Pinned to the theme's text colour
            // so the unselected state is as readable as the selected one.
            <ToggleButton key={h} value={h}
              sx={{ px: 1.2, py: 0.2, fontWeight: 700, color: 'text.primary' }}>{h}d</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip arrow title="Which forward window the 'vs BTC' column measures. The best/worst columns always cover the full window measured so far.">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: C.dim, cursor: 'help' }} />
        </Tooltip>

        <TextField select size="small" label="Grade" value={grade}
          onChange={e => setGrade(e.target.value)} sx={{ minWidth: 110 }}>
          <MenuItem value="">All</MenuItem>
          {['A', 'A-', 'B+', 'B', 'B-', 'C', 'D'].map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>

        <TextField select size="small" label="Direction" value={direction}
          onChange={e => setDirection(e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="">Both</MenuItem>
          <MenuItem value="LONG">Long</MenuItem>
          <MenuItem value="SHORT">Short</MenuItem>
        </TextField>

        <Tooltip arrow title={
          'How much forward history a row must already have to appear in the table. It is a ' +
          'MATURITY filter, not a time window: "5+ closed bars" means five daily bars have closed ' +
          'since the idea was recorded. Delta stamps daily bars at 00:00 UTC and the run fires at ' +
          '12:30, so the newest night of ideas legitimately has zero for about 36 hours — which is why ' +
          'the default is 1+ rather than "any". The maturity ledger below still counts every row, ' +
          'including the ones this filter hides.'
        }>
          <TextField select size="small" label="Measured over" value={minBars}
            onChange={e => setMinBars(Number(e.target.value))} sx={{ minWidth: 150 }}>
            <MenuItem value={0}>Any maturity</MenuItem>
            <MenuItem value={1}>1+ closed bars</MenuItem>
            <MenuItem value={5}>5+ closed bars</MenuItem>
            <MenuItem value={10}>Fully matured (10)</MenuItem>
          </TextField>
        </Tooltip>

        <TextField size="small" label="Symbol" value={symbol}
          onChange={e => setSymbol(e.target.value)} sx={{ minWidth: 130 }} />

        <Tooltip arrow title={
          'Narrows the table to the handful of ideas that actually reached the board each ' +
          'night — five at most, ranked by grade then conviction. Everything else here was ' +
          'assessed or scanned but never surfaced. Turn it on to judge what the board PUT IN ' +
          'FRONT OF YOU; leave it off to judge the whole engine including what it set aside.'
        }>
        <Chip size="small" label="Board only" onClick={() => setSurfacedOnly(v => !v)}
          sx={{
            fontWeight: 700, cursor: 'pointer',
            bgcolor: surfacedOnly ? C.accent : 'transparent',
            // The accent is light in dark mode, dark in light mode — so the readable text
            // colour flips with it rather than being white in both.
            color: surfacedOnly ? C.onSolid : C.dim,
            border: `1px solid ${C.accentBorder}`,
          }} />
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
          disabled={downloading || !rows.length} onClick={exportCsv}>
          CSV
        </Button>
      </Box>

      {/* ── By grade — the breakdown the replace-my-alerts decision needs ─── */}
      {/* ── What the engine is doing right now ───────────────────────────
          Recomputed on every load from the same buckets rendered below, so the sentence and the
          cards can never disagree. Every claim carries the n it rests on. */}
      {data?.insight && (
        <Box sx={{
          p: 1.2, mb: 2, borderRadius: 1,
          border: `1px solid ${data.insight.findings.length ? C.accentBorder : C.dimMuted}`,
          bgcolor: data.insight.findings.length ? C.accentMuted : C.dimMuted,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: C.accent, display: 'block' }}>
            What the engine is doing right now
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.3, lineHeight: 1.5 }}>
            {data.insight.headline}
          </Typography>
          {!!data.insight.findings.length && (
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mt: 0.8 }}>
              {data.insight.findings.map(f => (
                <Tooltip key={`${f.kind}-${f.label}`} arrow
                  title={`${f.n} rows resolved at ${data.insight.horizonDays} days — at or above the ${data.insight.minSample} needed before a bucket's number should be acted on.`}>
                  <Chip size="small" sx={{
                    height: 20, fontSize: '0.66rem', fontWeight: 700,
                    bgcolor: (f.meanRelPct ?? 0) > 0 ? C.bullMuted : C.bearMuted,
                    color: (f.meanRelPct ?? 0) > 0 ? C.bull : C.bear,
                  }}
                    label={`${f.kind === 'CALL' ? (STATE_LABEL[f.label] || f.label) : f.label} ${f.meanRelPct != null && f.meanRelPct > 0 ? '+' : ''}${f.meanRelPct ?? '—'}% · n=${f.n}`} />
                </Tooltip>
              ))}
            </Box>
          )}
          {!!data.insight.belowSample.length && (
            <Typography variant="caption" sx={{ color: C.dim, display: 'block', mt: 0.7, lineHeight: 1.45 }}>
              <b>Not enough data to judge yet</b> (measured, but under {data.insight.minSample} rows at
              this horizon): {data.insight.belowSample
                .map(b => `${b.kind === 'CALL' ? (STATE_LABEL[b.label] || b.label) : b.label} n=${b.n}`)
                .join(' · ')}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.6 }}>
          By grade
          <Tooltip arrow title={
            `Replacing your existing alerting is a per-grade decision, not a single yes or no. If ` +
            `A and A- earn their place and C and D do not, the right move is to surface the top ` +
            `grades and keep the deterministic alerts underneath. A grade needs ${minN} resolved ` +
            `outcomes before its number means anything.` +
            `

These counts will not match the Scorecard's exactly, and should not: this tab ` +
            `applies the filters above it — the maturity filter defaults to 1+ closed bars — and ` +
            `folds repeat scans of the same decision into one row. The Scorecard counts every ` +
            `resolved candidate. Both count only rows resolved AT the selected horizon, so the ` +
            `difference is the filtering, not the arithmetic.`
          }>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, ml: 0.5, cursor: 'help', verticalAlign: 'middle' }} />
          </Tooltip>
          <HelpLink to="ai-desk/performance" variant="icon" label="How every column here is measured" />
        </Typography>
        {!data?.byGrade.length ? (
          <Typography variant="caption" sx={{ color: C.dim }}>
            No graded row has a measured outcome yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.byGrade.map(b => <BucketCard key={b.label} b={b} C={C} minN={minN} horizon={horizon} />)}
          </Box>
        )}
      </Box>

      {/* ── By direction — 2/3 of this book is short, so never blend them ─── */}
      {!!data?.byDirection.some(b => b.n > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.6 }}>
            By direction
            <Tooltip arrow title="Long and short are separated because this book is roughly two-thirds short. A single blended average would hide whichever side is carrying the result.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, ml: 0.5, cursor: 'help', verticalAlign: 'middle' }} />
            </Tooltip>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.byDirection.filter(b => b.n > 0).map(b =>
              <BucketCard key={b.label} b={b} C={C} minN={minN} horizon={horizon} />)}
          </Box>
        </Box>
      )}

      {/* ── By call — what the model actually SAID, which nothing rendered before ─ */}
      {!!data?.byState?.some(b => b.n > 0) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.6 }}>
            By call
            <Tooltip arrow title="The state the model returned — act now, on pullback, wait for confirmation, watch, avoid. This is the bucket that answers 'is the AVOID call actually avoiding losers', which neither grade nor direction can.">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, ml: 0.5, cursor: 'help', verticalAlign: 'middle' }} />
            </Tooltip>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.byState.filter(b => b.n > 0).map(b => (
              <BucketCard key={b.label} b={{ ...b, label: STATE_LABEL[b.label] || b.label }}
                C={C} minN={minN} horizon={horizon} />
            ))}
          </Box>
        </Box>
      )}

      {/* ── Maturity ledger ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: C.dim, fontWeight: 700 }}>Maturity:</Typography>
        <Tooltip arrow title="Measured across the full 10-day window.">
          <Chip size="small" label={`${counts.COMPLETE} complete`}
            sx={{ height: 19, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.bullMuted, color: C.bull }} />
        </Tooltip>
        <Tooltip arrow title="Some closed bars measured, but the window has not finished. Real, but provisional.">
          <Chip size="small" label={`${counts.MATURING} still maturing`}
            sx={{ height: 19, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.warnMuted, color: C.warn }} />
        </Tooltip>
        <Tooltip arrow title="No forward bar has closed yet. Delta stamps daily bars at 00:00 UTC and the run fires at 12:30, so the first fully-closed bar after a decision arrives about 36 hours later — a new candidate is expected to sit here for a day.">
          <Chip size="small" label={`${counts.PENDING} not measurable yet`}
            sx={{ height: 19, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.dimMuted, color: C.dim }} />
        </Tooltip>
        {counts.UNSCOREABLE > 0 && (
          <Tooltip arrow title="The anchor price was stored too coarsely to compute a return from — captured before the price-precision fix. Scoring these would fabricate a move, so they are excluded rather than estimated.">
            <Chip size="small" label={`${counts.UNSCOREABLE} unscoreable`}
              sx={{ height: 19, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.bearMuted, color: C.bear }} />
          </Tooltip>
        )}
        {minBars > 0 && counts.PENDING > 0 && (
          <Typography variant="caption" sx={{ color: C.dim }}>
            · {counts.PENDING} newer {counts.PENDING === 1 ? 'row is' : 'rows are'} hidden by the
            maturity filter
          </Typography>
        )}
        {!!data && data.totalBeforeGrouping > (data.rows.length) && (
          <Tooltip arrow title="Several runs a day scan the same symbol against the same closed daily bar. Those are one decision recorded more than once, so they are folded into a single row rather than counted repeatedly in every average.">
            <Typography variant="caption" sx={{ color: C.dim, cursor: 'help' }}>
              {data.totalBeforeGrouping} scans folded into {data.rows.length} decisions
            </Typography>
          </Tooltip>
        )}
      </Box>

      {/* ── The table ────────────────────────────────────────────────────── */}
      {!rows.length ? (
        <MuiAlert severity="info" variant="outlined">
          No alerts match these filters.
        </MuiAlert>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow>
                <Th k="createdAt" label="When" {...{ sortKey, sortDesc, sortBy }} />
                <Th k="symbol" label="Symbol" {...{ sortKey, sortDesc, sortBy }} />
                <TableCell sx={{ fontWeight: 800 }}>Dir</TableCell>
                <Th k="grade" label="Grade" {...{ sortKey, sortDesc, sortBy }} />
                <TableCell sx={{ fontWeight: 800 }}>Called</TableCell>
                <Th k="bestPct" label="Best" align="right" {...{ sortKey, sortDesc, sortBy }}
                  hint="The best this POSITION reached, on closing prices — signed for its direction, so a short that fell is positive. A price you could plausibly have exited at." />
                <Th k="worstPct" label="Worst" align="right" {...{ sortKey, sortDesc, sortBy }}
                  hint="The worst it reached. Shown beside Best because a peak you could only have collected by sitting through a deep drawdown is a different proposition." />
                <TableCell sx={{ fontWeight: 800 }} align="right">
                  <Tooltip arrow title="The intraday extreme rather than a close. Real, but not reliably obtainable — you would have had to be watching at the right moment.">
                    <span>Peak (intraday)</span>
                  </Tooltip>
                </TableCell>
                <Th k="daysToBest" label="Days to best" align="right" {...{ sortKey, sortDesc, sortBy }}
                  hint="A 40% peak you had to hold 19 days for is a different product from one that peaked in 2." />
                <Th k="relPct" label="vs BTC" align="right" {...{ sortKey, sortDesc, sortBy }}
                  hint="Return minus Bitcoin's over the same window, signed for direction. On this universe nearly everything is a levered Bitcoin bet, so +8% while BTC did +9% is a losing idea." />
                <Th k="barsAvailable" label="Measured" align="right" {...{ sortKey, sortDesc, sortBy }}
                  hint="Closed daily bars this row has been measured over, out of the 10-day window." />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const provisional = r.maturity === 'PENDING' || r.maturity === 'UNSCOREABLE';
                return (
                  <TableRow key={r.id} hover sx={{ opacity: provisional ? 0.55 : 1 }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.72rem' }}>
                      {r.createdAt.slice(0, 10)}
                      <span style={{ color: C.dim }}> {r.createdAt.slice(11, 16)}</span>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {r.symbol}
                      {r.seenTimes > 1 && (
                        <Tooltip arrow title={`Scanned ${r.seenTimes} times against the same closed bar — one decision, counted once.`}>
                          <Chip size="small" label={`×${r.seenTimes}`}
                            sx={{ height: 15, fontSize: '0.55rem', ml: 0.5, bgcolor: C.dimMuted, color: C.dim }} />
                        </Tooltip>
                      )}
                      {r.surfaced && (
                        <Tooltip arrow title="Reached the board.">
                          <Chip size="small" label="board"
                            sx={{ height: 15, fontSize: '0.55rem', ml: 0.5, bgcolor: C.accentMuted, color: C.accent }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.direction ? (
                        <Chip size="small" label={r.direction}
                          sx={{
                            height: 17, fontSize: '0.6rem', fontWeight: 700,
                            bgcolor: r.direction === 'LONG' ? C.bullMuted : C.bearMuted,
                            color: r.direction === 'LONG' ? C.bull : C.bear,
                          }} />
                      ) : <span style={{ color: C.dim }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{r.grade || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.72rem' }}>
                      {r.state ? (STATE_LABEL[r.state] || r.state)
                        : <span style={{ color: C.dim }}>RockBand only</span>}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: pctColor(r.bestPct),
                    }}>
                      {r.bestPct == null ? '—' : fmt.pct(r.bestPct)}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', color: pctColor(r.worstPct),
                    }}>
                      {r.worstPct == null ? '—' : fmt.pct(r.worstPct)}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums', color: C.dim, fontSize: '0.72rem',
                    }}>
                      {r.bestHighPct == null ? '—' : fmt.pct(r.bestHighPct)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.daysToBest ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: pctColor(r.relPct),
                    }}>
                      {r.relPct == null ? '—' : fmt.pct(r.relPct)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.maturity === 'UNSCOREABLE' ? (
                        <Tooltip arrow title={r.note || 'Not scoreable.'}>
                          <span style={{ color: C.bear, fontSize: '0.7rem' }}>n/a</span>
                        </Tooltip>
                      ) : (
                        <Tooltip arrow title={
                          r.maturity === 'COMPLETE' ? 'Full 10-day window measured.'
                            : r.maturity === 'PENDING'
                              ? 'No forward bar has closed yet. Expected for a candidate under ~36 hours old.'
                              : `Provisional — ${r.barsAvailable} of 10 bars measured so far.`
                        }>
                          <span style={{
                            color: r.maturity === 'COMPLETE' ? C.bull
                              : r.maturity === 'PENDING' ? C.dim : C.warn,
                            fontSize: '0.72rem', fontWeight: 700,
                          }}>
                            {r.barsAvailable}/10
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ color: C.dim, display: 'block' }}>
        Anchored on the price the model was actually shown at decision time, never on a price
        looked up afterwards — scoring against information it never had would flatter the record.
      </Typography>

      {/* The OTHER AI's record, moved here from its own page. Both "did the AI add value?"
          surfaces now sit on one tab instead of on two pages that never referenced each other. */}
      <AiReliabilityPanel />
    </Box>
  );
};

/** One grade or direction, with its sample size stated as plainly as its average. */
/**
 * One bucket's result.
 *
 * It leads with `meanRelPct` against `nRel` — the figures that MOVE when the horizon does. It used
 * to lead with `meanBestPct` against `n`, and both of those cover the whole window measured so
 * far, so pressing 1d / 5d / 10d changed only the small win-rate text at the bottom and the
 * control read as dead. It is not dead: on the live book grade C's win rate walks 50% -> 59% ->
 * 59% and LONG walks 52% -> 69% -> 70% across those three presses.
 *
 * Leading with the horizon figure also reconciles this card with the Scorecard, which has always
 * counted only rows resolved at the horizon — the same "By grade" heading was reading C n=103
 * here and C n=67 there.
 */
const BucketCard: React.FC<{ b: AiPerformanceBucket; C: any; minN: number; horizon: number }> =
  ({ b, C, minN, horizon }) => {
  const underpowered = b.nRel < minN;
  const col = b.meanRelPct == null ? C.dim : b.meanRelPct > 0 ? C.bull : C.bear;
  return (
    <Tooltip arrow title={
      `${b.label} over ${horizon} day${horizon === 1 ? '' : 's'}: ${b.meanRelPct ?? '—'}% against Bitcoin ` +
      `on ${b.nRel} row${b.nRel === 1 ? '' : 's'} resolved at this horizon, win rate ${b.winRate ?? '—'}%. ` +
      `Signed by the direction proposed, so a short that fell counts as a gain. ` +
      `Separately, across the whole window measured so far: mean best ${b.meanBestPct ?? '—'}%, ` +
      `median ${b.medianBestPct ?? '—'}%, mean worst ${b.meanWorstPct ?? '—'}%, typical days to peak ` +
      `${b.medianDaysToBest ?? '—'}, over ${b.n} measured decisions — those do not change with the horizon.` +
      (underpowered ? ` Under ${minN} rows at this horizon — too few to act on.` : '')
    }>
      <Box sx={{
        px: 1.2, py: 0.7, borderRadius: 0.8, minWidth: 132,
        border: `1px solid ${underpowered ? C.dimMuted : `${col}55`}`,
        bgcolor: `${col}12`,
        // An under-powered bucket is drawn deliberately flat: it should not read as a finding
        // at a glance just because its average happens to be large.
        opacity: underpowered ? 0.75 : 1,
      }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
          {b.label}
          <Typography component="span" variant="caption" sx={{ color: C.dim, ml: 0.6, fontWeight: 600 }}>
            n={b.nRel}{underpowered ? ` / ${minN}` : ''}
          </Typography>
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: col, fontVariantNumeric: 'tabular-nums' }}>
          {b.meanRelPct == null ? '—' : fmt.pct(b.meanRelPct)}
        </Typography>
        <Typography variant="caption" sx={{ color: C.dim, display: 'block' }}>
          vs BTC over {horizon}d · win {b.winRate ?? '—'}%
        </Typography>
        <Typography variant="caption" sx={{ color: C.dim, display: 'block', opacity: 0.85 }}>
          best {b.meanBestPct == null ? '—' : fmt.pct(b.meanBestPct)} · worst{' '}
          {b.meanWorstPct == null ? '—' : fmt.pct(b.meanWorstPct)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const Th: React.FC<{
  k: SortKey; label: string; align?: 'left' | 'right'; hint?: string;
  sortKey: SortKey; sortDesc: boolean; sortBy: (k: SortKey) => void;
}> = ({ k, label, align, hint, sortKey, sortDesc, sortBy }) => {
  const inner = (
    <TableSortLabel active={sortKey === k} direction={sortDesc ? 'desc' : 'asc'}
      onClick={() => sortBy(k)}>
      {label}
    </TableSortLabel>
  );
  return (
    <TableCell align={align} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
      {hint ? <Tooltip arrow title={hint}><span>{inner}</span></Tooltip> : inner}
    </TableCell>
  );
};

export default AiPerformancePanel;
